import { useCallback, useRef, useState } from 'react'

/**
 * Lokaal bewerken van iets dat van de server komt (een notitie, een huidplan), zonder dat een
 * verversing op de achtergrond, een mislukte opslag of een optimistische update het getypte
 * overschrijft.
 *
 * - Niets bewerkt (`local === null`): het veld volgt de server.
 * - Wel bewerkt: wat je typte blijft staan, wat de server ook doet. Verandert de server intussen
 *   echt (ander apparaat), dan meldt `external` dat, met `discard` om die versie over te nemen.
 * - Opslaan gelukt: weer de server volgen, tenzij je intussen verder typte.
 * - Opslaan mislukt: je tekst blijft staan om het opnieuw te proberen.
 */

export type EditBuffer<T> = {
  /** Wat de gebruiker typte; `null` = niets, volg de server. */
  local: T | null
  /** De serverwaarde waarop de bewerking begon (voor "intussen elders gewijzigd"). */
  from: T | null
}

export const EMPTY_BUFFER: EditBuffer<never> = { local: null, from: null }

const same = (a: unknown, b: unknown) => a === b || JSON.stringify(a) === JSON.stringify(b)

export function bufferView<T>(b: EditBuffer<T>, server: T) {
  const editing = b.local !== null
  return {
    value: b.local ?? server,
    dirty: editing && !same(b.local, server),
    external: editing && b.from !== null && !same(server, b.from) && !same(server, b.local),
  }
}

export const editBuffer = <T,>(b: EditBuffer<T>, server: T, fn: (value: T) => T): EditBuffer<T> => ({
  local: fn(b.local ?? server),
  from: b.local === null ? server : b.from,
})

/** Na een opslagpoging met `sent`. Gelukt: de server heeft `sent`; verder getypt blijft lokaal. */
export const afterSave = <T,>(b: EditBuffer<T>, sent: T, ok: boolean): EditBuffer<T> => {
  if (!ok) return b
  if (b.local === null || same(b.local, sent)) return EMPTY_BUFFER
  return { local: b.local, from: sent }
}

/** `resetKey`: bij een andere klant (of ander item) opnieuw beginnen. */
export function useEditBuffer<T>(server: T, resetKey: string) {
  const [state, setState] = useState<{ key: string; buffer: EditBuffer<T> }>({ key: resetKey, buffer: EMPTY_BUFFER })
  let buffer = state.buffer
  if (state.key !== resetKey) {
    // Tijdens het renderen, zodat er nooit één beeld met de tekst van de vorige klant verschijnt.
    buffer = EMPTY_BUFFER
    setState({ key: resetKey, buffer })
  }
  const view = bufferView(buffer, server)

  const serverRef = useRef(server)
  serverRef.current = server
  const valueRef = useRef(view.value)
  valueRef.current = view.value

  const edit = useCallback((fn: (value: T) => T) => setState((s) => ({ ...s, buffer: editBuffer(s.buffer, serverRef.current, fn) })), [])
  /** Een eerdere invoer terugzetten (bijv. na "Ongedaan maken"). */
  const restore = useCallback((value: T) => setState((s) => ({ ...s, buffer: { local: value, from: serverRef.current } })), [])
  /** De versie van de server nemen; wat er getypt was, verdwijnt. */
  const discard = useCallback(() => setState((s) => ({ ...s, buffer: EMPTY_BUFFER })), [])
  const commit = useCallback(async (save: (value: T) => Promise<boolean>) => {
    const sent = valueRef.current
    const ok = await save(sent)
    setState((s) => ({ ...s, buffer: afterSave(s.buffer, sent, ok) }))
    return ok
  }, [])

  return { ...view, edit, restore, discard, commit }
}
